import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { getPageContent } from "@/lib/pageContent";

/** Relativo a modelo/estatico/ (<base href>) → pasta editorial/assets/img */
const DEFAULT_BANNERS = [
  {
    desktop: "../../editorial/assets/img/baner1.webp",
    mobile: "../../editorial/assets/img/baner1-mobile.webp",
    alt: "Criação de capa profissional",
  },
  {
    desktop: "../../editorial/assets/img/baner2.webp",
    mobile: "../../editorial/assets/img/baner2-mobile.webp",
    alt: "Correção ortográfica profissional",
  },
];

const MOBILE_MQ = "(max-width: 1023px)";

export default function EditorialBanner() {
  const page = getPageContent();
  const banners = Array.isArray(page?.heroBanners) && page.heroBanners.length
    ? page.heroBanners
    : DEFAULT_BANNERS;

  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const n = banners.length;

  const go = useCallback((dir) => {
    setIdx((i) => (i + dir + n) % n);
  }, [n]);

  const goTo = useCallback((i) => setIdx(i), []);

  useEffect(() => {
    if (paused || n < 2) return undefined;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 5200);
    return () => clearInterval(t);
  }, [paused, n]);

  if (!n) return null;
  const slide = banners[idx];

  return (
    <section
      id="inicio"
      className="relative bg-[#F8F7F4] pt-[calc(4.75rem+1rem)] lg:pt-[calc(5.5rem+0.75rem)]"
      data-testid="editorial-banner-section"
      aria-label="Banner principal"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div
        className="relative w-full overflow-hidden bg-[#0b1220]
          aspect-[825/1024] lg:aspect-[2100/749]"
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={idx}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.45 }}
            className="absolute inset-0"
          >
            <picture className="absolute inset-0 block w-full h-full">
              <source media={MOBILE_MQ} srcSet={slide.mobile || slide.desktop} type="image/webp" />
              <img
                src={slide.desktop}
                alt={slide.alt || ""}
                width={2100}
                height={749}
                decoding="async"
                fetchPriority={idx === 0 ? "high" : "low"}
                className="absolute inset-0 w-full h-full object-cover object-center"
                draggable={false}
              />
            </picture>
          </motion.div>
        </AnimatePresence>

        {n > 1 && (
          <>
            <button
              type="button"
              data-testid="editorial-banner-prev"
              aria-label="Banner anterior"
              onClick={() => go(-1)}
              className="absolute left-2.5 sm:left-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/90 text-neutral-900 shadow-md flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              type="button"
              data-testid="editorial-banner-next"
              aria-label="Próximo banner"
              onClick={() => go(1)}
              className="absolute right-2.5 sm:right-4 top-1/2 -translate-y-1/2 z-10 w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-white/90 text-neutral-900 shadow-md flex items-center justify-center hover:bg-white transition-colors"
            >
              <ChevronRight size={20} />
            </button>

            <div
              className="absolute bottom-3 sm:bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2"
              role="tablist"
              aria-label="Slides do banner"
            >
              {banners.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={i === idx}
                  aria-label={`Slide ${i + 1}`}
                  data-testid={`editorial-banner-dot-${i}`}
                  onClick={() => goTo(i)}
                  className={`h-2 rounded-full transition-all ${
                    i === idx ? "w-7 bg-white" : "w-2 bg-white/45 hover:bg-white/70"
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
